import { useCallback } from 'react';
import { PostgrestError } from '@supabase/supabase-js';
import { supabaseData } from '../../utils/supabase-data-client';
import { Note } from '../../types/notes';
import { useCacheStore } from './useCacheStore';
import { useCacheFirstQuery } from './useCacheFirstQuery';

interface UseNotesReturn {
  data: Note[] | undefined;
  loading: boolean;
  error: PostgrestError | null;
  refetch: () => Promise<void>;
}

/**
 * Cache-first list of active (non-trashed) notes in a category.
 * Mirrors useBookmarksByCategory; additionally excludes soft-deleted notes.
 */
const useNotes = (categoryId: string | null | undefined): UseNotesReturn => {
  const cache = useCacheStore();

  const cacheReader = cache.getNotes;

  const cacheFilter = useCallback(
    (items: Note[]) =>
      items
        .filter((n) => n.category_id === categoryId && n.deleted_at === null)
        .sort((a, b) => a.note_title.localeCompare(b.note_title)),
    [categoryId],
  );

  const networkFetch = useCallback(async () => {
    const { data, error } = await supabaseData
      .from('notes')
      .select<'*', Note>('*')
      .is('deleted_at', null)
      .order('note_title');

    if (data) {
      cache.setNotes(data as Note[]);
      const filtered = (data as Note[]).filter(
        (n) => n.category_id === categoryId,
      );
      return { data: filtered, error };
    }

    return { data: data as Note[] | null, error };
  }, [cache, categoryId]);

  const { data, loading, error, refetch } = useCacheFirstQuery<Note>({
    cacheReader,
    cacheFilter,
    networkFetch,
    enabled: !!categoryId,
    queryKey: categoryId ?? null,
  });

  return { data, loading, error, refetch };
};

export default useNotes;
